import CampoPergunta from "../components/CampoPergunta";
import { montarPerguntaComPrint } from "../lib/perguntaComPrint";
import { useSugestoes } from "../hooks/Sugestoes";
import ExibirPerguntasFrequentesPorSlug from "../components/ExibirPerguntasFrequentesPorSlug";
import { useEffect, useState } from "react";
import { usePergunta } from "../contexts/PerguntaContext";
import { useLocation } from "wouter";
import { EmpreenderApps } from "../files/AppsArray";
import { Assunto, Assuntos, AssuntosPadrao, AssuntosPorApp } from "../files/AssuntosPorApp";
import {
  CreditCard,
  Download,
  Heart,
  type LucideIcon,
  Package,
  Plug,
  Settings2,
  ShieldCheck,
  TriangleAlert,
  Truck,
  UserRound,
} from "lucide-react";
import SuporteIcon from "../empreenderUI/ui/appsIcons/SuporteIcon";
import { useSalvarConversaNoStorage } from "../hooks/SalvarConversaNoStorage";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import SEO from "../components/SEO";
import SeletorApp from "../components/SeletorApp";
import { rota } from "../lib/rotas";

// Ícone de cada assunto (pílulas da tela inicial).
const ICONES_ASSUNTO: Record<Assunto, LucideIcon> = {
  planos: CreditCard,
  importacao: Download,
  integracoes: Plug,
  configuracao: Settings2,
  cancelamento: ShieldCheck,
  conta: UserRound,
  erros: TriangleAlert,
  frete: Truck,
  pedidos: Package,
};

type TelaInicialProps = {
  appSlug: string;
  appName: string;
  // Modo embutido: primeiro nome enviado pelo painel que abriu o "Ajuda".
  saudacaoNome?: string;
  // Modo embutido no painel de um app (Sak, Lily…): a pessoa pergunta sobre aquele app, sem troca.
  permitirTrocaApp?: boolean;
};

function TelaInicial({ appSlug, appName, saudacaoNome, permitirTrocaApp = true }: TelaInicialProps) {
  const { pergunta, setPergunta, conversaAtual, setErroPergunta, setContextoPendente } =
    usePergunta();
  const [, setLocation] = useLocation();
  const [inputValue, setInputValue] = useState("");
  const { salvarConversa } = useSalvarConversaNoStorage();

  // Encontrar o aplicativo considerando o idioma
  const dadosApp = EmpreenderApps.find(
    (item) => item.name === appName || item.namees === appName
  );

  const { t } = useTranslation();
  const idiomaAtual = i18n.language;
  const idioma = idiomaAtual === "es" ? "es" : "pt";
  const enviarPergunta = (texto: string, textoImagem?: string) => {
    const { exibicao, api, extras } = montarPerguntaComPrint(texto, textoImagem, t);
    if (!exibicao || pergunta) return;

    setErroPergunta(null);
    setContextoPendente(null);
    setPergunta(api);
    salvarConversa(appSlug, conversaAtual, { usuario: exibicao }, undefined, undefined, undefined, extras);

    const slugAmigavel = dadosApp?.slugamigavel || "";

    setLocation(rota(`/${slugAmigavel}/chat/${conversaAtual}/${idiomaAtual}`));
  };

  // Verificando se é a linguagem 'es' e usando os campos namees e sluges
  const appNameCorrect =
    idiomaAtual === "es" ? dadosApp?.namees ?? "" : dadosApp?.name ?? "";
  const appSlugCorrect =
    idiomaAtual === "es" ? dadosApp?.sluges ?? "" : dadosApp?.slug ?? "";

  const assuntos =
    AssuntosPorApp[dadosApp?.slugamigavel || ""] || AssuntosPadrao;

  const { paraAutocompletar } = useSugestoes(appSlugCorrect, idioma);

  useEffect(() => {
    setInputValue("");
  }, [appSlug]);

  return (
    <div id="tela-inicial">
      <SEO
        titleKey="seo_home_title"
        descriptionKey="seo_home_description"
        args={{ appName: dadosApp?.name || t("suporte_empreender") }}
      />
      <section id="box-pergunta-inicio" className="painel-suporte">
        {/* Topo do painel (celular e painel "Ajuda"): o app da conversa. No site a pílula troca de
            app (opcional); no painel "Ajuda" de um app ela só mostra qual é. No computador o topo
            some: o ícone vai para cima da saudação e a troca de app, para dentro do campo. */}
        <header className="painel-topo">
          {permitirTrocaApp ? (
            <SeletorApp atual={dadosApp} />
          ) : (
            <div className="painel-marca">
              <span className="painel-marca-icone">{dadosApp?.icon || <SuporteIcon />}</span>
              <span>{appNameCorrect}</span>
            </div>
          )}
        </header>

        <div className="painel-icone-app" aria-hidden="true">
          {dadosApp?.icon || <SuporteIcon />}
        </div>

        {/* Topo enxuto: o app e a pergunta ("Super Favoritos: qual a dúvida?"), com o campo logo
            abaixo. No painel "Ajuda", a saudação com o nome de quem abriu. */}
        <div className="painel-intro">
          {saudacaoNome && (
            <p className="painel-eyebrow">{t("ola_nome", { nome: saudacaoNome })}</p>
          )}
          {/* "Super Favoritos: qual a dúvida?" numa linha no computador. No celular, o nome sai do
              título e fica no seletor do topo, grande (a primeira linha); aqui sobra a pergunta. */}
          <h1 id="titulo-como-posso-ajudar">
            <span className="titulo-app">{appNameCorrect || t("suporte_empreender")}</span>
            <span className="titulo-separador">: </span>
            <span className="titulo-pergunta">{t("qual_a_duvida")}</span>
          </h1>
        </div>

        <div id="box-input-duvida">
          <CampoPergunta
            id="input-duvida"
            valor={inputValue}
            aoMudar={setInputValue}
            aoEnviar={enviarPergunta}
            placeholder={t("digite_sua_duvida")}
            desabilitado={pergunta !== ""}
            opcoes={paraAutocompletar}
            idioma={idioma}
            extra={
              permitirTrocaApp ? (
                <div className="seletor-no-campo">
                  <SeletorApp atual={dadosApp} />
                </div>
              ) : undefined
            }
          />
        </div>

        <p className="assuntos-titulo">{t("assuntos_titulo")}</p>
        <div className="assuntos">
          {assuntos.map((chave) => {
            const Icone = ICONES_ASSUNTO[chave];
            return (
              <button
                key={chave}
                className="assunto"
                disabled={pergunta !== ""}
                onClick={() =>
                  enviarPergunta(
                    Assuntos[chave].pergunta[idioma].replace("{{app}}", appNameCorrect)
                  )
                }
              >
                {Icone && <Icone size={14} strokeWidth={1.6} aria-hidden="true" />}
                {Assuntos[chave].rotulo[idioma]}
              </button>
            );
          })}
        </div>

        <div id="box-categorias-tela-inicial">
          <ExibirPerguntasFrequentesPorSlug
            appSlug={appSlugCorrect}
            aoEscolher={enviarPergunta}
            desabilitado={pergunta !== ""}
            formato="lista"
          />
        </div>

        <footer className="painel-rodape">
          <Heart size={11} strokeWidth={1.6} aria-hidden="true" />
          <span>{t("rodape_central")}</span>
        </footer>
      </section>
    </div>
  );
}

export default TelaInicial;
